import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { STATUSES } from "@/lib/collections";
import { Search, Download, LogOut, Archive, ArchiveRestore, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
    const { user, logout, token, authHeaders } = useAuth();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        const params = {};
        if (q) params.q = q;
        if (statusFilter) params.status = statusFilter;
        params.archived = showArchived;
        try {
            const { data } = await api.get("/admin/submissions", { params, headers: authHeaders });
            setItems(data.items);
            const { data: s } = await api.get("/admin/submissions/stats", { headers: authHeaders });
            setStats(s);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }, [q, statusFilter, showArchived, authHeaders]);

    useEffect(() => { if (user) load(); }, [user, load]);

    if (user === null) return <div className="p-10 text-center text-slate-500">Chargement...</div>;
    if (!user || user.role !== "admin") return <Navigate to="/administration" replace/>;

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/submissions/${id}`, { status }, { headers: authHeaders });
            toast.success("Statut mis à jour");
            load();
            if (selected?.id === id) setSelected(s => ({ ...s, status }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const updateNotes = async (id, internal_notes) => {
        try {
            await api.patch(`/admin/submissions/${id}`, { internal_notes }, { headers: authHeaders });
            toast.success("Notes enregistrées");
            if (selected?.id === id) setSelected(s => ({ ...s, internal_notes }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const toggleArchive = async (id, archived) => {
        try {
            await api.patch(`/admin/submissions/${id}`, { archived }, { headers: authHeaders });
            toast.success(archived ? "Archivé" : "Rétabli");
            load();
            if (selected?.id === id) setSelected(s => ({ ...s, archived }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const downloadCSV = async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/export.csv`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "include",
            });
            if (!res.ok) throw new Error("Export impossible");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `soumissions-kgd-${new Date().toISOString().slice(0,10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e.message); }
    };

    const statusInfo = (k) => STATUSES.find(s => s.key === k) || STATUSES[0];

    return (
        <div className="min-h-screen bg-[#F7F8FA]">
            <div className="bg-[#071A33] text-white">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex items-center justify-between">
                    <div>
                        <div className="eyebrow">Espace administratif</div>
                        <h1 className="font-serif text-2xl mt-1">Tableau de bord — Ménage KGD</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={downloadCSV} data-testid="admin-export" className="btn-ghost-gold text-sm"><Download className="w-4 h-4"/> Exporter CSV</button>
                        <button onClick={logout} data-testid="admin-logout" className="text-sm text-white/80 hover:text-white flex items-center gap-1"><LogOut className="w-4 h-4"/> Déconnexion</button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label="Total" value={stats.total} color="#071A33"/>
                    <StatCard label="Actifs" value={stats.active} color="#075BC8"/>
                    <StatCard label="Nouveaux leads" value={stats.by_status.nouveau_lead} color="#138A3D"/>
                    <StatCard label="Gagnés" value={stats.by_status.gagne} color="#D4AF37"/>
                </div>
            )}

            {/* Filters */}
            <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-4 flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input data-testid="admin-search" placeholder="Rechercher (nom, courriel, ville, n°...)" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white" value={q} onChange={e => setQ(e.target.value)}/>
                </div>
                <select data-testid="admin-filter-status" className="rounded-lg border border-slate-300 bg-white px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">Tous les statuts</option>
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-700 px-3">
                    <input data-testid="admin-toggle-archived" type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}/>
                    Archivés
                </label>
            </div>

            {/* List */}
            <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1fr_2fr_1.5fr_1fr_1.4fr_auto] gap-3 px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b bg-slate-50">
                        <span>N°</span><span>Client</span><span>Contact</span><span>Ville</span><span>Statut</span><span></span>
                    </div>
                    {items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Aucune demande.</div>}
                    {items.map(it => {
                        const s = statusInfo(it.status);
                        return (
                            <div key={it.id} data-testid={`row-${it.id}`} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1.5fr_1fr_1.4fr_auto] gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 items-center">
                                <div className="text-xs font-mono text-slate-500">{it.request_number}</div>
                                <div>
                                    <div className="font-medium text-[#071A33]">{it.first_name} {it.last_name}</div>
                                    <div className="text-xs text-slate-500">{new Date(it.created_at).toLocaleString("fr-CA")}</div>
                                </div>
                                <div className="text-sm text-slate-700">
                                    <div>{it.email}</div>
                                    <div className="text-xs">{it.phone}</div>
                                </div>
                                <div className="text-sm text-slate-700">{it.city}</div>
                                <div>
                                    <select data-testid={`status-${it.id}`} value={it.status} onChange={e => updateStatus(it.id, e.target.value)}
                                        className="rounded-md border px-2 py-1 text-xs" style={{ borderColor: s.color, color: s.color }}>
                                        {STATUSES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                                    </select>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={() => setSelected(it)} data-testid={`open-${it.id}`} className="text-xs text-kgd-blue hover:underline">Ouvrir</button>
                                    <button onClick={() => toggleArchive(it.id, !it.archived)} data-testid={`archive-${it.id}`} className="text-xs text-slate-500 hover:text-slate-700" title={it.archived ? "Rétablir" : "Archiver"}>
                                        {it.archived ? <ArchiveRestore className="w-4 h-4"/> : <Archive className="w-4 h-4"/>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Detail Drawer */}
            {selected && <Detail sub={selected} onClose={() => setSelected(null)} onNotes={updateNotes} onStatus={updateStatus} onArchive={toggleArchive} authHeaders={authHeaders}/>}
        </div>
    );
}

function StatCard({ label, value, color }) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-serif text-3xl mt-1" style={{ color }}>{value}</div>
        </div>
    );
}

function Detail({ sub, onClose, onNotes, onStatus, onArchive, authHeaders }) {
    const [notes, setNotes] = useState(sub.internal_notes || "");
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
            <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xs font-mono text-slate-500">{sub.request_number}</div>
                        <h2 className="font-serif text-2xl text-[#071A33]">{sub.first_name} {sub.last_name}</h2>
                        <div className="text-xs text-slate-500">{new Date(sub.created_at).toLocaleString("fr-CA")}</div>
                    </div>
                    <button onClick={onClose} data-testid="detail-close" className="p-2"><X className="w-5 h-5"/></button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <Info label="Courriel" value={sub.email}/>
                    <Info label="Téléphone" value={sub.phone}/>
                    <Info label="Ville" value={sub.city}/>
                    <Info label="Adresse" value={sub.address || "—"}/>
                    <Info label="Type" value={sub.property_type}/>
                    <Info label="Collection" value={sub.collection || "—"}/>
                    <Info label="Établissement" value={sub.establishment_type || "—"}/>
                    <Info label="Pièces" value={sub.rooms || "—"}/>
                    <Info label="Salles de bain" value={sub.bathrooms || "—"}/>
                    <Info label="Fréquence" value={sub.frequency || "—"}/>
                    <Info label="Période" value={sub.preferred_date || "—"}/>
                    <Info label="Contact préféré" value={sub.contact_method}/>
                </div>
                <div className="mt-4">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Description</div>
                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{sub.description}</p>
                </div>
                {sub.photo_ids?.length > 0 && (
                    <div className="mt-4">
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Photos</div>
                        <div className="grid grid-cols-3 gap-2">
                            {sub.photo_ids.map(id => (
                                <a key={id} href={`${BACKEND_URL}/api/files/${id}`} target="_blank" rel="noreferrer" className="aspect-square rounded overflow-hidden border">
                                    <img src={`${BACKEND_URL}/api/files/${id}`} alt="Pièce jointe" className="w-full h-full object-cover"/>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
                <div className="mt-6">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Notes internes</div>
                    <textarea data-testid="detail-notes" rows="4" className="w-full border rounded-lg p-3 text-sm" value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => onNotes(sub.id, notes)}/>
                </div>
                <div className="mt-4 flex gap-2">
                    <button onClick={() => onArchive(sub.id, !sub.archived)} data-testid="detail-archive" className="btn-ghost-gold text-[#071A33] border-[#071A33]/30 text-sm">
                        {sub.archived ? "Rétablir" : "Archiver"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-slate-800">{value}</div>
        </div>
    );
}
